import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../firebase';
import { collection, addDoc, getDocs, doc, updateDoc, deleteDoc, orderBy, query } from 'firebase/firestore';
import { Container, Card, Form, Button, Spinner, Alert, Row, Col, InputGroup, ListGroup, Modal, ProgressBar } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';

// 都道府県リスト
const PREFECTURES = [
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
  "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県",
  "静岡県", "愛知県", "三重県", "滋賀県", "京都府", "大阪府", "兵庫県",
  "奈良県", "和歌山県", "鳥取県", "島根県", "岡山県", "広島県", "山口県",
  "徳島県", "香川県", "愛媛県", "高知県", "福岡県", "佐賀県", "長崎県",
  "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県"
];

// 【追加】全角数字を半角数字に変換するヘルパー関数
const toHalfWidth = (str) => {
  if (!str) return '';
  return str.replace(/[０-９]/g, (s) => {
    return String.fromCharCode(s.charCodeAt(0) - 0xFEE0);
  });
};

// 住所の文字数を計算するヘルパー関数
const calculateAddressLength = (address) => {
  const fullAddress = `${address.address1 || ''}${address.address2 || ''}${address.address3 || ''}`;
  let length = 0;
  for (let i = 0; i < fullAddress.length; i++) {
    length += fullAddress.charCodeAt(i).toString(16).length > 2 ? 2 : 1;
  }
  return length;
};


function ShippingAddressList() {
  const [addresses, setAddresses] = useState([]);

  // 新しいフィールドを含む空の住所オブジェクトを定義
  const emptyAddress = {
    recipientName: '',
    shippingCompanyName: '',
    branchName: '',
    departmentName: '',
    postalCode: '',
    address1: '',
    address2: '',
    address3: '',
    phoneNumber: ''
  };

  const [newAddress, setNewAddress] = useState({ ...emptyAddress });
  const [editingAddress, setEditingAddress] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showLimitModal, setShowLimitModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 都道府県入力エラー警告用のState (新規用・編集用)
  const [newPrefectureWarning, setNewPrefectureWarning] = useState('');
  const [editPrefectureWarning, setEditPrefectureWarning] = useState('');
  
  // 【追加】郵便番号入力エラー警告用のState (新規用・編集用)
  const [newZipCodeWarning, setNewZipCodeWarning] = useState('');
  const [editZipCodeWarning, setEditZipCodeWarning] = useState('');

  const userId = auth.currentUser?.uid;
  const MAX_ADDRESSES = 30;
  const MAX_ADDRESS_LENGTH = 150;

  const [newPhoneParts, setNewPhoneParts] = useState({ part1: '', part2: '', part3: '' });
  const [editPhoneParts, setEditPhoneParts] = useState({ part1: '', part2: '', part3: '' });

  const fetchAddresses = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const addressesCollectionRef = collection(db, 'addresses', userId, 'shipping_addresses');
      const q = query(addressesCollectionRef, orderBy('shippingCompanyName'), orderBy('recipientName'));
      const data = await getDocs(q);
      const fetchedAddresses = data.docs.map(doc => ({ ...doc.data(), id: doc.id }));
      setAddresses(fetchedAddresses);
    } catch (err) {
      setError('送付先リストの取得に失敗しました。');
      console.error(err);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    document.title = '送付先リスト管理 | My Order System';
    if (userId) {
      fetchAddresses();
    } else {
      setError('ユーザーがログインしていません。');
      setLoading(false);
    }
  }, [userId, fetchAddresses]);

  useEffect(() => {
    const { part1, part2, part3 } = newPhoneParts;
    setNewAddress(prev => ({ ...prev, phoneNumber: `${part1}-${part2}-${part3}` }));
  }, [newPhoneParts]);

  useEffect(() => {
    if (editingAddress) {
      const { part1, part2, part3 } = editPhoneParts;
      setEditingAddress(prev => ({ ...prev, phoneNumber: `${part1}-${part2}-${part3}` }));
    }
  }, [editPhoneParts]);

  const handleShowEditModal = (address) => {
    setEditingAddress(address);
    // モーダルを開くときにエラーをリセット
    setEditPrefectureWarning(''); 
    setEditZipCodeWarning(''); // 追加
    const parts = (address.phoneNumber || '').split('-');
    setEditPhoneParts({
      part1: parts[0] || '',
      part2: parts[1] || '',
      part3: parts[2] || '',
    });
    setShowEditModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditingAddress(null);
    setEditPhoneParts({ part1: '', part2: '', part3: '' });
    setEditPrefectureWarning('');
    setEditZipCodeWarning(''); // 追加
  };

  const handleNewAddressChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;

    // 【追加】新規登録時の郵便番号処理
    if (name === 'postalCode') {
      // 1. 全角数字を半角に変換
      const halfWidthValue = toHalfWidth(value);
      // 2. 数字以外を除去 (stateに保存する値)
      const numericValue = halfWidthValue.replace(/[^0-9]/g, '');
      
      // 7桁に制限してstateにセット
      processedValue = numericValue.slice(0, 7);

      // 3. 警告判定
      if (halfWidthValue !== numericValue) {
        setNewZipCodeWarning('半角数字のみ入力可能です。');
      } else if (processedValue.length > 0 && processedValue.length !== 7) {
        setNewZipCodeWarning('郵便番号は7桁で入力してください。');
      } else {
        setNewZipCodeWarning('');
      }
    }

    setNewAddress(prev => ({ ...prev, [name]: processedValue }));

    // 新規登録時の都道府県チェック
    if (name === 'address1') {
      if (processedValue && !PREFECTURES.includes(processedValue)) {
        setNewPrefectureWarning('正しい都道府県名を入力してください。');
      } else {
        setNewPrefectureWarning('');
      }
    }
  };

  const handleEditAddressChange = (e) => {
    const { name, value } = e.target;
    
    let processedValue = value;

    // 【追加】編集時の郵便番号処理
    if (name === 'postalCode') {
      const halfWidthValue = toHalfWidth(value);
      const numericValue = halfWidthValue.replace(/[^0-9]/g, '');
      processedValue = numericValue.slice(0, 7);

      if (halfWidthValue !== numericValue) {
        setEditZipCodeWarning('半角数字のみ入力可能です。');
      } else if (processedValue.length > 0 && processedValue.length !== 7) {
        setEditZipCodeWarning('郵便番号は7桁で入力してください。');
      } else {
        setEditZipCodeWarning('');
      }
    }

    setEditingAddress(prev => ({ ...prev, [name]: processedValue }));

    // 編集時の都道府県チェック
    if (name === 'address1') {
      if (processedValue && !PREFECTURES.includes(processedValue)) {
        setEditPrefectureWarning('正しい都道府県名を入力してください。');
      } else {
        setEditPrefectureWarning('');
      }
    }
  };

  const handlePhoneChange = (e, formType) => {
    const { name, value } = e.target;
    const sanitizedValue = value.replace(/[^0-9]/g, '');

    if (formType === 'new') {
      setNewPhoneParts(prev => ({ ...prev, [name]: sanitizedValue }));
    } else {
      setEditPhoneParts(prev => ({ ...prev, [name]: sanitizedValue }));
    }
  };

  const handleAddAddress = async (e) => {
    e.preventDefault();
    setError('');

    // 都道府県バリデーションチェック
    if (!PREFECTURES.includes(newAddress.address1)) {
        setError('都道府県が正しくありません。');
        setNewPrefectureWarning('正しい都道府県名を入力してください。');
        return;
    }
    
    // 【追加】郵便番号桁数チェック
    if (newAddress.postalCode.length !== 7) {
        setError('郵便番号は7桁で入力してください。');
        setNewZipCodeWarning('郵便番号は7桁で入力してください。');
        return;
    }

    if (!newAddress.recipientName || !newAddress.postalCode || !newAddress.address1 || !newAddress.address2 || !newAddress.phoneNumber) {
      setError('担当者名、郵便番号、住所1, 2、電話番号は必須です。');
      return;
    }
    if (calculateAddressLength(newAddress) > MAX_ADDRESS_LENGTH) {
      setError('住所の合計文字数が上限を超えています。');
      return;
    }
    if (addresses.length >= MAX_ADDRESSES) {
      setShowLimitModal(true);
      return;
    }
    try {
      const addressesCollectionRef = collection(db, 'addresses', userId, 'shipping_addresses');
      await addDoc(addressesCollectionRef, newAddress);
      setNewAddress({ ...emptyAddress });
      setNewPhoneParts({ part1: '', part2: '', part3: '' });
      setNewPrefectureWarning('');
      setNewZipCodeWarning(''); // 追加
      fetchAddresses();
    } catch (err) {
      setError('送付先の登録に失敗しました。');
      console.error(err);
    }
  };

  const handleDeleteAndRegister = async (addressIdToDelete) => {
    if (!PREFECTURES.includes(newAddress.address1)) {
        alert('新しい送付先の都道府県が正しくありません。');
        setNewPrefectureWarning('正しい都道府県名を入力してください。');
        return;
    }
    // 【追加】郵便番号チェック
    if (newAddress.postalCode.length !== 7) {
        alert('郵便番号は7桁で入力してください。');
        setNewZipCodeWarning('郵便番号は7桁で入力してください。');
        return;
    }

    try {
      const addressesCollectionRef = collection(db, 'addresses', userId, 'shipping_addresses');
      const addressToDeleteDoc = doc(db, 'addresses', userId, 'shipping_addresses', addressIdToDelete);
      await Promise.all([
        deleteDoc(addressToDeleteDoc),
        addDoc(addressesCollectionRef, newAddress)
      ]);
      setNewAddress({ ...emptyAddress });
      setNewPrefectureWarning('');
      setNewZipCodeWarning('');
      setShowLimitModal(false);
      fetchAddresses();
    } catch (err) {
      alert('処理に失敗しました。');
      console.error(err);
    }
  };

  const handleUpdateAddress = async () => {
    if (!editingAddress) return;

    // 編集保存時の都道府県バリデーションチェック
    if (!PREFECTURES.includes(editingAddress.address1)) {
        alert('都道府県が正しくありません。');
        setEditPrefectureWarning('正しい都道府県名を入力してください。');
        return;
    }
    // 【追加】郵便番号チェック
    if (editingAddress.postalCode.length !== 7) {
        alert('郵便番号は7桁で入力してください。');
        setEditZipCodeWarning('郵便番号は7桁で入力してください。');
        return;
    }

    if (calculateAddressLength(editingAddress) > MAX_ADDRESS_LENGTH) {
      alert('住所の合計文字数が上限を超えています。');
      return;
    }
    try {
      const addressDoc = doc(db, 'addresses', userId, 'shipping_addresses', editingAddress.id);
      const { id, ...dataToUpdate } = editingAddress;
      await updateDoc(addressDoc, dataToUpdate);
      handleCloseEditModal();
      fetchAddresses();
    } catch (err) {
      alert('送付先の更新に失敗しました。');
      console.error(err);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (window.confirm('この送付先を本当に削除しますか？')) {
      try {
        const addressDoc = doc(db, 'addresses', userId, 'shipping_addresses', id);
        await deleteDoc(addressDoc);
        fetchAddresses();
      } catch (err) {
        alert('送付先の削除に失敗しました。');
        console.error(err);
      }
    }
  };

  // 住所検索ハンドラ
  const handlePostalCodeSearch = useCallback(async (targetState, setStateFunction) => {
    if (targetState && targetState.postalCode && targetState.postalCode.length === 7) {
      try {
        const res = await axios.get('https://zipcloud.ibsnet.co.jp/api/search', { params: { zipcode: targetState.postalCode } });
        if (res.data.results) {
          const result = res.data.results[0];
          setStateFunction(prev => ({ ...prev, address1: result.address1, address2: result.address2 + result.address3 }));
          
          // 住所検索成功時は警告をクリアする
          if (setStateFunction === setNewAddress) {
             setNewPrefectureWarning('');
             setNewZipCodeWarning('');
          } else {
             setEditPrefectureWarning('');
             setEditZipCodeWarning('');
          }

        } else {
          alert('該当する住所が見つかりませんでした。');
        }
      } catch (err) {
        alert('住所の自動入力に失敗しました。');
      }
    } else {
      alert('7桁の郵便番号を入力してください。');
      // 桁数不足で検索ボタンを押した場合は警告を表示
      if (targetState.postalCode.length !== 7) {
          if (setStateFunction === setNewAddress) {
             setNewZipCodeWarning('郵便番号は7桁で入力してください。');
          } else {
             setEditZipCodeWarning('郵便番号は7桁で入力してください。');
          }
      }
    }
  }, []); 

  const AddressLengthIndicator = ({ address }) => {
    const length = calculateAddressLength(address);
    const variant = length > MAX_ADDRESS_LENGTH ? 'danger' : 'success';
    return (
      <div className="mt-2">
        <ProgressBar now={length} max={MAX_ADDRESS_LENGTH} variant={variant} />
        <small className={`d-block text-end ${variant === 'danger' ? 'text-danger' : 'text-muted'}`}>
          文字数: {length} / {MAX_ADDRESS_LENGTH}(半角:1文字、全角:2文字で計算)
        </small>
      </div>
    );
  };

  return (
    <main>
      <Container className="my-4">
        <Card className="shadow-sm">
          <Card.Header className="p-3 d-flex flex-column flex-md-row justify-content-md-between align-items-md-end">
            <h2 className="mb-2 mb-md-0">
              送付先リスト管理
            </h2>
            <Link to="/mainmenu">
              <small>
                メインメニューに戻る
              </small>
            </Link>
          </Card.Header>
          <Card.Body className="p-4">
            {
              error &&
              <Alert variant="danger">
                {error}
              </Alert>
            }
            <Alert variant="info">
              送付先は最大{MAX_ADDRESSES}件まで登録できます。現在の登録件数: {addresses.length}件
            </Alert>
            <Card className="mb-4 bg-light">
              <Card.Header>
                <h5>
                  新規送付先追加
                </h5>
              </Card.Header>
              <Card.Body>
                <Form onSubmit={handleAddAddress}>
                  <Row>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          会社名
                          <small className="text-muted">
                            (任意)
                          </small>
                        </Form.Label>
                        <Form.Control
                          className="custom-placeholder"
                          type="text"
                          name="shippingCompanyName"
                          value={newAddress.shippingCompanyName}
                          onChange={handleNewAddressChange}
                          placeholder="例: 旭興株式会社"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          支店名
                          <small className="text-muted">
                            (任意)
                          </small>
                        </Form.Label>
                        <Form.Control
                          className="custom-placeholder"
                          type="text"
                          name="branchName"
                          value={newAddress.branchName}
                          onChange={handleNewAddressChange}
                          placeholder="例: 東京支店"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          部署名
                          <small className="text-muted">
                            (任意)
                          </small>
                        </Form.Label>
                        <Form.Control
                          className="custom-placeholder"
                          type="text"
                          name="departmentName"
                          value={newAddress.departmentName}
                          onChange={handleNewAddressChange}
                          placeholder="例: 営業部"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          担当者名
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="recipientName"
                          value={newAddress.recipientName}
                          onChange={handleNewAddressChange}
                          required
                          placeholder="例: 旭興太郎"
                          className="custom-placeholder"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          電話番号
                        </Form.Label>
                        <Row className="g-2 align-items-center">
                          <Col>
                            <Form.Control
                              type="tel"
                              name="part1"
                              value={newPhoneParts.part1}
                              onChange={(e) => handlePhoneChange(e, 'new')}
                              maxLength="5"
                              placeholder="090"
                              className="custom-placeholder"
                            />
                          </Col>
                          <Col xs="auto">
                            -
                          </Col>
                          <Col>
                            <Form.Control
                              type="tel"
                              name="part2"
                              value={newPhoneParts.part2}
                              onChange={(e) => handlePhoneChange(e, 'new')}
                              maxLength="4"
                              placeholder="1234"
                              className="custom-placeholder"
                            />
                          </Col>
                          <Col xs="auto">
                            -
                          </Col>
                          <Col>
                            <Form.Control
                              type="tel"
                              name="part3"
                              value={newPhoneParts.part3}
                              onChange={(e) => handlePhoneChange(e, 'new')}
                              maxLength="4"
                              placeholder="5678"
                              className="custom-placeholder"
                            />
                          </Col>
                        </Row>
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          郵便番号
                        </Form.Label>
                        <InputGroup>
                          <Form.Control
                            type="text"
                            name="postalCode"
                            value={newAddress.postalCode}
                            onChange={handleNewAddressChange}
                            maxLength="7"
                            required
                            placeholder="例:5310076"
                            className="custom-placeholder"
                            // 【追加】警告がある場合に赤枠を表示
                            isInvalid={!!newZipCodeWarning}
                          />
                          <Button
                            variant="secondary"
                            onClick={() => handlePostalCodeSearch(newAddress, setNewAddress)}
                          >
                            住所検索
                          </Button>
                          {/* 【追加】警告メッセージの表示エリア */}
                          <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                            {newZipCodeWarning}
                          </Form.Control.Feedback>
                        </InputGroup>
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          住所1(都道府県)
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="address1"
                          value={newAddress.address1}
                          onChange={handleNewAddressChange}
                          required 
                          placeholder="例:大阪府"
                          className="custom-placeholder"
                          // 警告があれば赤枠
                          isInvalid={!!newPrefectureWarning}
                        />
                        {/* 警告メッセージ表示 */}
                        <Form.Control.Feedback type="invalid">
                          {newPrefectureWarning}
                        </Form.Control.Feedback>
                      </Form.Group></Col>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          住所2(市区町村)
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="address2"
                          value={newAddress.address2}
                          onChange={handleNewAddressChange}
                          required
                          placeholder="例:北区大淀中1-1-30"
                          className="custom-placeholder"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          住所3(ビル名・マンション名)
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="address3"
                          value={newAddress.address3}
                          onChange={handleNewAddressChange}
                          placeholder="例:梅田スカイビル　タワーウエスト 16F"
                          className="custom-placeholder"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <AddressLengthIndicator address={newAddress} />
                    </Col>
                  </Row>
                  <Button type="submit" variant="primary" className="mt-3">
                    リストに追加
                  </Button>
                </Form>
              </Card.Body>
            </Card>

            <h5 className="mt-4">
              登録済みリスト
            </h5>
            {
              loading ?
                <div className="text-center">
                  <Spinner animation="border" />
                </div>
                :
                (
                  <ListGroup variant="flush">
                    {addresses.map(address => (
                      <ListGroup.Item key={address.id} className="d-flex justify-content-between align-items-center flex-wrap">
                        <div className="flex-grow-1 me-3">
                          {(address.shippingCompanyName || address.branchName || address.departmentName) &&
                            <div className="fw-bold">
                              {address.shippingCompanyName} {address.branchName} {address.departmentName}
                            </div>
                          }
                          <div>
                            {address.recipientName}
                          </div>
                          <div className="text-muted small">
                            〒{address.postalCode} {address.address1}{address.address2}{address.address3} | TEL: {address.phoneNumber}
                          </div>
                        </div>
                        <div className="mt-2 mt-md-0">
                          <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => handleShowEditModal(address)}>
                            編集
                          </Button>
                          <Button variant="outline-danger" size="sm" onClick={() => handleDeleteAddress(address.id)}>
                            削除
                          </Button>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )
            }
            <hr className="my-4" />
            <Link to="/mainmenu">
              メインメニューに戻る
            </Link>
          </Card.Body>
        </Card>

        <Modal show={showEditModal} onHide={handleCloseEditModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              送付先編集
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {editingAddress && (
              <Form>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        会社名
                        <small className="text-muted">
                          (任意)
                        </small>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="shippingCompanyName"
                        value={editingAddress.shippingCompanyName || ''}
                        onChange={handleEditAddressChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        支店名
                        <small className="text-muted">
                          (任意)
                        </small>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="branchName"
                        value={editingAddress.branchName || ''}
                        onChange={handleEditAddressChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        部署名
                        <small className="text-muted">
                          (任意)
                        </small>
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="departmentName"
                        value={editingAddress.departmentName || ''}
                        onChange={handleEditAddressChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        担当者名
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="recipientName"
                        value={editingAddress.recipientName}
                        onChange={handleEditAddressChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        電話番号
                      </Form.Label>
                      <Row className="g-2 align-items-center">
                        <Col>
                          <Form.Control
                            type="tel"
                            name="part1"
                            value={editPhoneParts.part1}
                            onChange={(e) => handlePhoneChange(e, 'edit')}
                            maxLength="5"
                          />
                        </Col>
                        <Col xs="auto">
                          -
                        </Col>
                        <Col>
                          <Form.Control
                            type="tel"
                            name="part2"
                            value={editPhoneParts.part2}
                            onChange={(e) => handlePhoneChange(e, 'edit')}
                            maxLength="4"
                          />
                        </Col>
                        <Col xs="auto">
                          -
                        </Col>
                        <Col>
                          <Form.Control
                            type="tel"
                            name="part3"
                            value={editPhoneParts.part3}
                            onChange={(e) => handlePhoneChange(e, 'edit')}
                            maxLength="4"
                          />
                        </Col>
                      </Row>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        郵便番号
                      </Form.Label>
                      <InputGroup>
                        <Form.Control
                          type="text"
                          name="postalCode"
                          value={editingAddress.postalCode}
                          onChange={handleEditAddressChange}
                          maxLength="7"
                          // ★追加: 警告があれば赤枠
                          isInvalid={!!editZipCodeWarning}
                        />
                        <Button
                          variant="secondary"
                          onClick={() => handlePostalCodeSearch(editingAddress, setEditingAddress)}
                        >
                          住所検索
                        </Button>
                        {/* ★追加: 警告メッセージ表示 */}
                        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
                          {editZipCodeWarning}
                        </Form.Control.Feedback>
                      </InputGroup>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        住所1(都道府県)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="address1"
                        value={editingAddress.address1}
                        onChange={handleEditAddressChange}
                        // ★追加: 警告があれば赤枠
                        isInvalid={!!editPrefectureWarning}
                      />
                      {/* ★追加: 警告メッセージ表示 */}
                      <Form.Control.Feedback type="invalid">
                        {editPrefectureWarning}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        住所2(市区町村)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="address2"
                        value={editingAddress.address2}
                        onChange={handleEditAddressChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        住所3(ビル名・マンション名)
                      </Form.Label>
                      <Form.Control
                        type="text"
                        name="address3"
                        value={editingAddress.address3}
                        onChange={handleEditAddressChange}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <AddressLengthIndicator address={editingAddress} />
                  </Col>
                </Row>
              </Form>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditModal}>
              キャンセル
            </Button>
            <Button variant="primary" onClick={handleUpdateAddress}>
              保存
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showLimitModal} onHide={() => setShowLimitModal(false)} size="lg">
          <Modal.Header closeButton>
            <Modal.Title className="text-danger">
              登録上限に達しました
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              送付先は最大{MAX_ADDRESSES}件まで登録できます。新しい送付先を登録するには、既存のリストからどれか1つを削除してください。
            </p>
            <ListGroup style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {addresses.map(addr => (
                <ListGroup.Item key={addr.id} className="d-flex justify-content-between align-items-center">
                  <div>
                    <strong>
                      {addr.recipientName}
                    </strong>
                    <br />
                    <small>
                      〒{addr.postalCode} {addr.address1}{addr.address2}{addr.address3}
                    </small>
                  </div>
                  <Button variant="danger" onClick={() => handleDeleteAndRegister(addr.id)}>
                    削除して登録
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowLimitModal(false)}>
              キャンセル
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </main>
  );
}

export default ShippingAddressList;